import { useState, useEffect } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import '../Machinegroup/machinegroup.css';
import PlantGroupAdd from './plantgroupadd';
import PlantGroupEdit from './plantgroupedit';
import { customerbasedshift, getCustomerUsers } from '../../Services/app/operatorservice';
import { saveMasterListSafely } from '../../Services/app/masterservice';
import Swal from 'sweetalert2';

const PlantGroup = ({ IsInGroupReg = false }) => {
  const [plantGroups, setPlantGroups] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogOpenCount, setDialogOpenCount] = useState(0);
  const customerId = localStorage.getItem('CustomerID');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);

  const handleCloseAddDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsAddDialogOpen(false);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchPlantGroups();
  };

  const handleCloseEditDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsEditDialogOpen(false);
    setEditDialogData(null);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchPlantGroups();
  };

  useEffect(() => {
    fetchPlantGroups();
    fetchMachineGroups();
  }, []);

  const fetchPlantGroups = async () => {
    customerbasedshift(customerId, 'plantgroups')
      .then((data) => {
        setPlantGroups(data[0]?.value || []);
      })
      .catch((error) => {
        console.error('Error fetching plant groups:', error);
        setPlantGroups([]);
      });
  };

  const fetchMachineGroups = async () => {
    customerbasedshift(customerId, 'machinegroups')
      .then((data) => {
        setMachineGroups(data[0]?.value || []);
      })
      .catch((error) => {
        console.error('Error fetching machine groups:', error);
        setMachineGroups([]);
      });
  };

  const groupNameByCode = new Map(machineGroups.map((g) => [g.code, g.name || g.code]));

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  const handleOpenEditDialog = (plant) => {
    setEditDialogData(plant);
    setIsEditDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  // A Plant that still has Machine Groups, Components, Reasons or Users tied
  // to it shouldn't just vanish out from under them — those references are
  // what drive Plant-scoped access and record numbering (see
  // filterGroupsForUserWithPlants / filterByPlantAccess), so deleting it
  // silently would orphan them with no one deciding that. Find every such
  // dependent first and refuse the delete until they're all reassigned.
  const findPlantGroupDependents = async (plant) => {
    const [usersRes, componentRes, reasonRes, qualityReasonRes] = await Promise.all([
      getCustomerUsers(customerId),
      customerbasedshift(customerId, 'component'),
      customerbasedshift(customerId, 'reason'),
      customerbasedshift(customerId, 'qualityreason'),
    ]);

    const blockingGroups = plant.groupCodes || [];

    const allUsers = usersRes?.data || [];
    const blockingUsers = allUsers.filter((user) => {
      let desc = {};
      try {
        desc = user.additionalInfo?.description ? JSON.parse(user.additionalInfo.description) : {};
      } catch {
        desc = {};
      }
      return Array.isArray(desc.plantAccess) && desc.plantAccess.includes(plant.code);
    });

    const allComponents = componentRes?.[0]?.value || [];
    const blockingComponents = allComponents.filter((c) => c.plant_code === plant.code);

    const allReasons = [...(reasonRes?.[0]?.value || []), ...(qualityReasonRes?.[0]?.value || [])];
    const blockingReasons = allReasons.filter((r) => r.plant_code === plant.code);

    return { blockingGroups, blockingComponents, blockingReasons, blockingUsers };
  };

  const handleDeletePlantGroup = async (row) => {
    let blockingGroups = [];
    let blockingComponents = [];
    let blockingReasons = [];
    let blockingUsers = [];
    try {
      ({ blockingGroups, blockingComponents, blockingReasons, blockingUsers } = await findPlantGroupDependents(row));
    } catch (error) {
      console.error('Error checking Plant dependents:', error);
      Swal.fire('Error', 'Could not verify whether this plant is still in use. Please try again.', 'error');
      return;
    }

    if (blockingGroups.length > 0 || blockingComponents.length > 0 || blockingReasons.length > 0 || blockingUsers.length > 0) {
      const countLine = (label, count) => count > 0 && `<b>${label}:</b> ${count}`;
      const lines = [
        countLine('Machine Groups', blockingGroups.length),
        countLine('Components', blockingComponents.length),
        countLine('Reasons', blockingReasons.length),
        countLine('Users', blockingUsers.length),
      ].filter(Boolean).join('<br/>');
      Swal.fire({
        icon: 'warning',
        title: 'Cannot delete this Plant',
        html: `It is still assigned to the following — reassign them to a different Plant/Group first:<br/><br/>${lines}`,
      });
      return;
    }

    Swal.fire({
      title: 'Are you sure you want to delete this Plant?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (!result.isConfirmed) {
        Swal.fire('Cancelled', '', 'info');
        return;
      }
      try {
        let savedList = plantGroups;
        await saveMasterListSafely({
          customerId,
          key: 'plantgroups',
          baselineCount: plantGroups.length,
          mutate: (existingPlants) => {
            const updated = existingPlants.filter((p) => p.code !== row.code);
            savedList = updated;
            return updated;
          },
        });
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Plant deleted successfully.',
          timer: 1500,
          showConfirmButton: false,
        });
        setPlantGroups(savedList);
        fetchPlantGroups();
      } catch (error) {
        console.error('Error deleting Plant:', error);
        Swal.fire('Error!', 'Failed to delete Plant: ' + error.message, 'error');
      }
    });
  };

  return (
    <div className="pages" style={{
      paddingBlockStart: IsInGroupReg ? '0px' : '40px',
    }}>
      <div className="pagecontents">
        <div className="left-labels" style={{
          padding: IsInGroupReg ? '0 2rem 1rem 0' : '1rem 2rem 1rem 0',
        }}>
          <div className="shift-content-1">
            {IsInGroupReg ? (<h6>Create New</h6>) : (<h5>Plant List</h5>)}
            <div className="add_new">
              <Tooltip title="Add Plant">
                <IconButton className="circle" onClick={handleOpenAddDialog}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="idle_reason_list">
          {Array.isArray(plantGroups) && plantGroups.length > 0 ? (
            plantGroups
              .slice()
              .sort((a, b) => Number(a.code) - Number(b.code))
              .map((plant, idx) => {
                const itemId = plant.code || idx;
                const groupNames = (plant.groupCodes || []).map((c) => groupNameByCode.get(c) || c);
                const VISIBLE_GROUP_COUNT = 3;
                const visibleGroupNames = groupNames.slice(0, VISIBLE_GROUP_COUNT);
                const hiddenGroupCount = groupNames.length - visibleGroupNames.length;
                return (
                  <div className="idle_reason_item" key={itemId} data-id={plant.code}>
                    <div className="icons">
                      <span className="icon-text">{plant.code}</span>
                    </div>
                    <h3 className="reason-text">
                      {plant.name}
                      <Tooltip title={groupNames.length > 0 ? groupNames.join(', ') : ''}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            color: '#888',
                            fontWeight: 400,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {groupNames.length > 0 ? (
                            <>
                              {visibleGroupNames.join(', ')}
                              {hiddenGroupCount > 0 && ` +${hiddenGroupCount}`}
                            </>
                          ) : (
                            'No machine groups assigned'
                          )}
                        </span>
                      </Tooltip>
                    </h3>
                    <div className="user_action">
                      <ul>
                        <li>
                          <Tooltip title="Edit Plant">
                            <IconButton onClick={() => handleOpenEditDialog(plant)}>
                              <EditIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                        <li>
                          <Tooltip title="Delete Plant">
                            <IconButton onClick={() => handleDeletePlantGroup(plant)}>
                              <DeleteIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              })
          ) : (
            <div style={{ margin: '2rem', color: '#888' }}>
              No plants found.
            </div>
          )}
        </div>
      </div>
      <PlantGroupAdd
        open={isAddDialogOpen}
        handleClose={handleCloseAddDialog}
        handleAdd={handleCloseAddDialog}
        dialogOpenCount={dialogOpenCount}
        datasource={plantGroups}
        allMachineGroups={machineGroups}
        customerId={customerId}
        setDatasource={setPlantGroups}
      />

      {isEditDialogOpen && editDialogData && (
        <PlantGroupEdit
          open={isEditDialogOpen}
          handleClose={handleCloseEditDialog}
          handleAdd={handleCloseEditDialog}
          dialogData={editDialogData}
          dialogOpenCount={dialogOpenCount}
          datasource={plantGroups}
          allMachineGroups={machineGroups}
          customerId={customerId}
          setDatasource={setPlantGroups}
        />
      )}
    </div>
  );
};

export default PlantGroup;
